/**
 * Notification System Types
 *
 * Types for the ITSM notification system including ownership-based routing,
 * smart grouping, and activity-triggered notifications.
 */

import type { TicketStatus, AssignmentGroup } from './ticket';

// ============================================
// NOTIFICATION TYPE
// ============================================

export const NotificationType = {
  /** IT added a public comment */
  TICKET_UPDATE: 'ticket_update',
  /** IT asked user a question */
  QUESTION_RECEIVED: 'question_received',
  /** Ticket status changed (to specific statuses) */
  STATUS_CHANGE: 'status_change',
  /** Ticket has been resolved */
  TICKET_RESOLVED: 'ticket_resolved',
  /** User replied to ticket */
  NEW_REPLY: 'new_reply',
  /** User answered a question */
  QUESTION_ANSWERED: 'question_answered',
  /** User reopened a ticket */
  TICKET_REOPENED: 'ticket_reopened',
  /** Ticket escalated to new group */
  ESCALATION: 'escalation',
  /** New unassigned ticket in queue */
  QUEUE_ALERT: 'queue_alert',
  /** Assignment changed */
  ASSIGNMENT_CHANGE: 'assignment_change',
} as const;
export type NotificationType = (typeof NotificationType)[keyof typeof NotificationType];

// ============================================
// RECIPIENT TYPE
// ============================================

export const RecipientType = {
  /** End user who submitted the ticket */
  USER: 'user',
  /** Individual IT technician */
  TECH: 'tech',
  /** Team/Assignment group */
  TEAM: 'team',
} as const;
export type RecipientType = (typeof RecipientType)[keyof typeof RecipientType];

// ============================================
// NOTIFICATION PRIORITY (for sorting/filtering)
// ============================================

export const NotificationPriority = {
  /** Questions, escalations, reopened tickets */
  HIGH: 'high',
  /** Status changes, new replies */
  NORMAL: 'normal',
  /** Queue alerts, general updates */
  LOW: 'low',
} as const;
export type NotificationPriority = (typeof NotificationPriority)[keyof typeof NotificationPriority];

// ============================================
// NOTIFICATION ENTITY
// ============================================

export interface Notification {
  /** Unique identifier */
  id: string;

  /** Who receives this notification */
  recipientId: string;

  /** Type of recipient (user, tech, or team) */
  recipientType: RecipientType;

  /** Related ticket ID */
  ticketId: string;

  /** Related ticket number for display */
  ticketNumber: string;

  /** Originating activity ID (for deduplication) */
  activityId: string;

  /** Type of notification */
  type: NotificationType;

  /** Notification priority for sorting */
  priority: NotificationPriority;

  /** Short title (e.g., "New reply on INC0012345") */
  title: string;

  /** Detailed message */
  message: string;

  /** Whether notification has been read */
  isRead: boolean;

  /** Group key for smart grouping (ticketId + time window) */
  groupKey: string;

  /** When notification was created */
  createdAt: Date;

  /** When notification was read (null if unread) */
  readAt: Date | null;

  /** Actor who triggered the notification */
  actorId: string;
  actorName: string;

  /** For ESCALATION: new assignment group */
  assignmentGroup?: AssignmentGroup;

  /** For STATUS_CHANGE: new status */
  newStatus?: TicketStatus;
}

// ============================================
// NOTIFICATION GROUP (for display)
// ============================================

export interface NotificationGroup {
  /** Group key */
  groupKey: string;

  /** Ticket ID for navigation */
  ticketId: string;

  /** Ticket number for display */
  ticketNumber: string;

  /** Primary notification (most recent or highest priority) */
  primaryNotification: Notification;

  /** All notifications in this group */
  notifications: Notification[];

  /** Total count in group */
  count: number;

  /** Whether any notification in group is unread */
  hasUnread: boolean;

  /** Most recent timestamp in group */
  latestAt: Date;

  /** Combined message summary */
  summary: string;
}

// ============================================
// DTOs
// ============================================

export interface CreateNotificationDTO {
  recipientId: string;
  recipientType: RecipientType;
  ticketId: string;
  ticketNumber: string;
  activityId: string;
  type: NotificationType;
  priority?: NotificationPriority;
  title: string;
  message: string;
  actorId: string;
  actorName: string;
  assignmentGroup?: AssignmentGroup;
  newStatus?: TicketStatus;
}

export interface UpdateNotificationDTO {
  isRead?: boolean;
  readAt?: Date | null;
}

// ============================================
// NOTIFICATION FILTER OPTIONS
// ============================================

export interface NotificationFilterOptions {
  /** Filter by recipient */
  recipientId?: string;
  recipientType?: RecipientType;
  /** Filter by notification types */
  types?: NotificationType[];
  /** Filter by read status */
  isRead?: boolean;
  /** Filter by date range */
  dateRange?: {
    start: Date;
    end: Date;
  };
  /** Limit results */
  limit?: number;
  /** Include read notifications in grouped results */
  includeRead?: boolean;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Generate a group key for smart grouping.
 * Groups notifications within 5-minute windows for the same ticket.
 */
export function generateGroupKey(ticketId: string, timestamp: Date): string {
  const WINDOW_MS = 5 * 60 * 1000; // 5 minutes
  const windowStart = Math.floor(timestamp.getTime() / WINDOW_MS) * WINDOW_MS;
  return `${ticketId}-${windowStart}`;
}

/**
 * Get notification priority based on type.
 */
export function getNotificationPriority(type: NotificationType): NotificationPriority {
  switch (type) {
    case NotificationType.QUESTION_RECEIVED:
    case NotificationType.ESCALATION:
    case NotificationType.TICKET_REOPENED:
      return NotificationPriority.HIGH;

    case NotificationType.STATUS_CHANGE:
    case NotificationType.TICKET_RESOLVED:
    case NotificationType.NEW_REPLY:
    case NotificationType.QUESTION_ANSWERED:
    case NotificationType.ASSIGNMENT_CHANGE:
      return NotificationPriority.NORMAL;

    case NotificationType.QUEUE_ALERT:
    case NotificationType.TICKET_UPDATE:
    default:
      return NotificationPriority.LOW;
  }
}

/**
 * Get display icon name for notification type.
 */
export function getNotificationIcon(type: NotificationType): string {
  switch (type) {
    case NotificationType.QUESTION_RECEIVED:
      return 'help_outline';
    case NotificationType.QUESTION_ANSWERED:
      return 'question_answer';
    case NotificationType.TICKET_RESOLVED:
      return 'check_circle';
    case NotificationType.ESCALATION:
      return 'trending_up';
    case NotificationType.NEW_REPLY:
      return 'reply';
    case NotificationType.TICKET_REOPENED:
      return 'refresh';
    case NotificationType.QUEUE_ALERT:
      return 'notifications';
    case NotificationType.ASSIGNMENT_CHANGE:
      return 'person_add';
    case NotificationType.STATUS_CHANGE:
    case NotificationType.TICKET_UPDATE:
    default:
      return 'update';
  }
}

/**
 * Format relative time for notification display.
 */
export function formatNotificationTime(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Get a human-readable label for notification type.
 */
export function getNotificationTypeLabel(type: NotificationType): string {
  switch (type) {
    case NotificationType.TICKET_UPDATE:
      return 'Update';
    case NotificationType.QUESTION_RECEIVED:
      return 'Question';
    case NotificationType.STATUS_CHANGE:
      return 'Status Change';
    case NotificationType.TICKET_RESOLVED:
      return 'Resolved';
    case NotificationType.NEW_REPLY:
      return 'Reply';
    case NotificationType.QUESTION_ANSWERED:
      return 'Answer';
    case NotificationType.TICKET_REOPENED:
      return 'Reopened';
    case NotificationType.ESCALATION:
      return 'Escalation';
    case NotificationType.QUEUE_ALERT:
      return 'Queue Alert';
    case NotificationType.ASSIGNMENT_CHANGE:
      return 'Assignment';
    default:
      return 'Notification';
  }
}

/**
 * Check if a notification type should be considered high priority.
 */
export function isHighPriorityNotification(type: NotificationType): boolean {
  return getNotificationPriority(type) === NotificationPriority.HIGH;
}

/**
 * Status changes that should notify users.
 */
export const USER_NOTIFIABLE_STATUSES: TicketStatus[] = [
  'In Progress',
  'Waiting',
  'Resolved',
  'Closed',
];

/**
 * Check if a status change should trigger a user notification.
 */
export function shouldNotifyUserOnStatusChange(newStatus: TicketStatus): boolean {
  return USER_NOTIFIABLE_STATUSES.includes(newStatus);
}
